import React, { useEffect, useState } from "react";
import { Tag, Tooltip } from "antd";
import {
  FileTextOutlined,
  CheckCircleTwoTone,
  ExclamationCircleTwoTone,
  ClockCircleOutlined,
  FolderOpenOutlined,
  FolderOutlined,
  CloseCircleTwoTone,
  SyncOutlined,
  SmileTwoTone,
} from "@ant-design/icons";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { Flex, Spin } from "antd";
import { RootState } from "../store/store";
import TransactionFlowDefaultTemplate from "./others/transactionFlowDefaultTemplate";
import FormatDate from "./masters/DateFormater";

const DefaultDashboardPage = () => {
  const [refresh, setRefresh] = useState(false);
  const { data: allowedRoutes } = useSelector(
    (state: RootState) => state.allowedRoutes
  );
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!allowedRoutes || !allowedRoutes.length) return;

    setIsLoading(false);
  }, [allowedRoutes]);

  if (isLoading)
    return (
      <Flex align="center" gap="middle" className="pageLoader">
        <Spin size="large" className="m-auto" />
      </Flex>
    );

  return (
    <TransactionFlowDefaultTemplate
      moduleKey="view-tenders-list"
      title="Tenders"
      columns={[
        {
          title: "Tender ID",
          dataIndex: "tenderNumber",
          key: "tenderNumber",
          fixed: "left",
          width: 170,
          render(text: any, record: any) {
            return (
              <>
                <Tooltip title={"Click To View Tender Details"}>
                  <span
                    className="inlineTableCellLink"
                    onClick={() => {
                      navigate(`/view-tender/${record?.tenderId}`, {
                        state: {
                          tenderId: record?.tenderId,
                          bidsSummary: record?.bidsSummary,
                          supplierBidStatus: record?.supplierBidStatus,
                        },
                      });
                    }}
                  >
                    {text}
                  </span>
                </Tooltip>
              </>
            );
          },
        },
        {
          title: "Description",
          dataIndex: "description",
          key: "description",
          render: (text: string) => (
            <Tooltip title={text}>
              <span
                style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: 270,
                  display: "inline-block",
                }}
              >
                {text}
              </span>
            </Tooltip>
          ),
        },
        {
          title: "Type",
          dataIndex: "tenderType",
          key: "tenderType",
          width: 130,
          render: (type: string) => (
            <Tag
              color={type === "Open" ? "green" : "blue"}
              icon={
                type === "Open" ? <FolderOpenOutlined /> : <FolderOutlined />
              }
            >
              {type}
            </Tag>
          ),
        },
        {
          title: "Category",
          dataIndex: "tenderFor",
          width: 130,
          key: "tenderFor",
          render: (tenderFor: string) => (
            <Tag color={tenderFor === "Material" ? "volcano" : "cyan"}>
              {tenderFor}
            </Tag>
          ),
        },
        {
          title: "Status",
          dataIndex: "supplierBidStatus",
          key: "supplierBidStatus",
          width: 180,
          render: (status: string, record: any) => {
            const statusMap: Record<
              string,
              { color: string; icon: JSX.Element }
            > = {
              "New Tender": {
                color: "blue",
                icon: <ClockCircleOutlined />,
              },
              Interested: {
                color: "cyan",
                icon: <CheckCircleTwoTone twoToneColor="#52c41a" />,
              },
              "Not Interested": {
                color: "default",
                icon: <SmileTwoTone twoToneColor="#d9d9d9" />,
              },
              Responded: {
                color: "purple",
                icon: <FileTextOutlined />,
              },
              Negotiation: {
                color: "orange",
                icon: <SyncOutlined />,
              },
              "Partially Awarded": {
                color: "gold",
                icon: <ExclamationCircleTwoTone twoToneColor="#faad14" />,
              },
              Awarded: {
                color: "green",
                icon: <CheckCircleTwoTone twoToneColor="#52c41a" />,
              },
              Cancelled: {
                color: "red",
                icon: <CloseCircleTwoTone twoToneColor="#ff4d4f" />,
              },
            };
            const { color, icon } = statusMap[status] || {
              color: "default",
              icon: <FileTextOutlined />,
            };
            return (
              <Tag color={color} icon={icon}>
                {status}
              </Tag>
            );
          },
        },
        {
          title: "Site",
          dataIndex: "siteName",
          key: "siteName",
          width: 250,
          render: (text: string) => (
            <Tooltip title={text}>
              <span
                style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: 230,
                  display: "inline-block",
                }}
              >
                {text || "-"}
              </span>
            </Tooltip>
          ),
        },
        {
          title: "EOI Deadline",
          dataIndex: "lastDateForExpressingInterest",
          key: "lastDateForExpressingInterest",
          render: (date: any) => FormatDate(date),
          width: 120,
        },
        {
          title: "Submission Deadline",
          dataIndex: "lastSubmissionDate",
          key: "lastSubmissionDate",
          render: (date: any) => FormatDate(date),
          width: 160,
        },
      ]}
      refresh={refresh}
      extra={{
        tableScrollX: 1550,
        dateRange: false,
        excelExport: false,
        dataFilterTootip: "Filtering based on Purchase Order date",
      }}
    />
  );
};

export default DefaultDashboardPage;
